import { createFileRoute } from '@tanstack/react-router'

import type { InventoryLevelListItem } from '@/api/admin-inventory-types'
import { preloadGetInventoryReportSummary, useGetInventoryReportSummary } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { Text } from '@/components/base/text'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'

import { useReportLowStockColumnDefs } from './-components'

export const Route = createFileRoute('/_authenticated/_general/inventory/reports')({
	component: InventoryReportsPage,
	loader: async ({ context }) => {
		await preloadGetInventoryReportSummary(context, { query: {} })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.Reports()
	}
})

function InventoryReportsPage() {
	const { L } = useI18n()
	const report = useGetInventoryReportSummary({})
	const columnDefs = useReportLowStockColumnDefs()

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={L.Inventory.Reports()} />
				<div className='grid grid-cols-1 gap-3 px-6 pb-4 sm:grid-cols-2 lg:grid-cols-4'>
					<Metric label={L.Inventory.Summary.TotalSkus()} value={report.data?.totalSkus ?? 0} />
					<Metric label={L.Inventory.Column.QuantityOnHand()} value={report.data?.totalQuantityOnHand ?? 0} />
					<Metric label={L.Inventory.Summary.LowStock()} value={report.data?.lowStockCount ?? 0} />
					<Metric label={L.Inventory.Summary.OutOfStock()} value={report.data?.outOfStockCount ?? 0} />
				</div>
				<Text className='px-6 pb-2 text-sm text-ui-fg-subtle'>{L.Inventory.Summary.LowStock()}</Text>
				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<InventoryLevelListItem>
						className='w-full min-w-[600px] flex-1'
						data={report.data?.topLowStock}
						columnUtilities={{}}
						columnDefs={columnDefs}
						animateRows
						suppressCellFocus
						rowToRouterConfig={level => ({
							to: '/inventory/$inventoryLevelUuid/adjust',
							params: { inventoryLevelUuid: level.uuid }
						})}
					/>
				</div>
			</Container>
		</SingleColumnPage>
	)
}

const Metric = ({ label, value }: { label: string; value: number }) => (
	<div className='rounded-lg border border-ui-border-base bg-ui-bg-subtle px-4 py-3'>
		<Text className='text-sm text-ui-fg-subtle'>{label}</Text>
		<Text className='text-2xl font-semibold text-ui-fg-base'>{value}</Text>
	</div>
)
